import { useState } from "react";
import axios from "axios";
import { Sparkles, Mail, CalendarCheck, LayoutDashboard, ArrowRight } from "lucide-react";

const API = `${window.location.origin}/api`;
const DEMO_SLUG = "bookd-demo";

/**
 * Placeholder landing page for the SaaS root domain (bookdbeyond.com/).
 *
 * Renders ONLY when no tenant slug is present in the URL — i.e. someone
 * visiting the bare host without a salon-specific path. Existing tenant
 * URLs (e.g. /mamabeauty, /lauren-aesthetics) are unaffected and continue
 * to render the tenant's public homepage.
 *
 * This page is intentionally minimal — it will be replaced by the full
 * marketing site (features, reviews, signup, etc.) when the design lands.
 */
export default function ComingSoon() {
  const [enteringAdmin, setEnteringAdmin] = useState(false);
  const [demoError, setDemoError] = useState("");

  const enterAdminDemo = async () => {
    setDemoError("");
    setEnteringAdmin(true);
    try {
      const res = await axios.post(`${API}/demo/admin-login`);
      const { token, tenant_id } = res.data;
      localStorage.setItem(`salonos_token_${tenant_id}`, token);
      window.location.href = `/${tenant_id}/admin`;
    } catch (e) {
      setDemoError("Demo is warming up — please try again in a moment.");
      setEnteringAdmin(false);
    }
  };

  return (
    <div
      data-testid="bookd-coming-soon"
      className="min-h-screen flex items-center justify-center px-6 py-16 relative overflow-hidden"
      style={{
        background:
          'radial-gradient(60% 60% at 20% 10%, rgba(248,189,212,0.45) 0%, rgba(255,247,250,0) 60%), radial-gradient(40% 40% at 90% 90%, rgba(255,217,200,0.35) 0%, rgba(255,247,250,0) 55%), #faf7f6',
      }}
    >
      <div className="max-w-xl w-full text-center relative">
        <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-white/70 backdrop-blur-sm rounded-full text-xs font-medium text-foreground/70 mb-6 border border-white/60">
          <Sparkles size={12} className="text-primary" />
          The booking platform built for beauty
        </div>

        <h1
          className="text-5xl sm:text-6xl lg:text-7xl font-semibold tracking-tight mb-5"
          style={{ fontFamily: 'Playfair Display, serif', color: '#3d3433' }}
        >
          B<span style={{ color: '#c9a89c' }}>∞</span>kd
        </h1>

        <p
          className="text-xl sm:text-2xl font-medium mb-4"
          style={{ fontFamily: 'Playfair Display, serif', color: '#3d3433' }}
        >
          Beauty bookings, beyond.
        </p>

        <p className="text-sm sm:text-base text-foreground/60 leading-relaxed max-w-md mx-auto mb-10">
          A modern, AI-powered booking platform for salons and aestheticians.
          We're putting the finishing touches on something beautiful — full site
          launching soon.
        </p>

        <a
          href="mailto:hello@bookdbeyond.com?subject=B%E2%88%9Ekd%20Early%20Access"
          data-testid="coming-soon-contact-link"
          className="inline-flex items-center gap-2 px-6 py-3 rounded-2xl text-sm font-medium transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg"
          style={{
            background: '#3d3433',
            color: '#ffffff',
            textDecoration: 'none',
          }}
        >
          <Mail size={16} />
          Get early access
        </a>

        {/* Live demo — Lumière Nail Bar, a fully seeded fictional salon */}
        <div
          className="mt-10 p-6 sm:p-7 rounded-3xl border relative overflow-hidden"
          style={{ background: 'linear-gradient(135deg, #171412 0%, #2a231c 100%)', borderColor: 'rgba(202,161,75,0.35)' }}
          data-testid="live-demo-banner"
        >
          <div
            className="absolute -top-10 -right-10 w-40 h-40 rounded-full opacity-20 blur-2xl"
            style={{ background: '#caa14b' }}
          />
          <div className="relative">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-white/10 backdrop-blur-sm rounded-full text-[11px] font-medium text-[#e9d9b6] mb-4 border border-white/10">
              <Sparkles size={11} style={{ color: '#caa14b' }} />
              Live demo · Lumière Nail Bar
            </div>
            <p className="text-lg sm:text-xl font-medium mb-1 text-white" style={{ fontFamily: 'Playfair Display, serif' }}>
              See B∞kd in action
            </p>
            <p className="text-sm text-white/60 mb-5 max-w-sm">
              A fully populated demo salon — real-looking clients, appointments and reports. No signup needed.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <a
                href={`/${DEMO_SLUG}/book`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="demo-booking-link"
                className="group inline-flex items-center justify-center gap-2 px-5 py-3 rounded-2xl text-sm font-medium transition-all duration-200 hover:-translate-y-0.5"
                style={{ background: '#caa14b', color: '#171412', textDecoration: 'none' }}
              >
                <CalendarCheck size={16} />
                View Booking Demo
                <ArrowRight size={14} className="transition-transform duration-200 group-hover:translate-x-0.5" />
              </a>
              <button
                onClick={enterAdminDemo}
                disabled={enteringAdmin}
                data-testid="demo-admin-login-button"
                className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-2xl text-sm font-medium transition-all duration-200 hover:-translate-y-0.5 border border-white/20 text-white hover:bg-white/10 disabled:opacity-60 disabled:cursor-wait"
              >
                <LayoutDashboard size={16} />
                {enteringAdmin ? 'Entering…' : 'Enter Admin Demo'}
              </button>
            </div>
            {demoError && (
              <p className="text-xs text-red-300 mt-3" data-testid="demo-admin-login-error">{demoError}</p>
            )}
          </div>
        </div>

        <p className="text-xs text-foreground/40 mt-8">
          Already on B∞kd? Visit your salon's direct link to book.
        </p>
      </div>
    </div>
  );
}
